import { cn } from "@/lib/utils";
import {
  RangeFilterType,
  SelectorFilterType,
  UpdateFilterType,
} from "@/pages/Players/FiltersPanel/filterTypes";
import { topToPageSelector } from "@/redux/globalState/globalStateSlice";
import { MetaRating } from "@repo/shared/types";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import { APIStatus } from "../../../../api/APIStatus.type";
import { SavedSquad } from "../../../../api/privateRequests/squad-builder/get-squads";
import { formationsSelector } from "../../../../redux/loadedContent/loadedContentSlice";
import { PlayerRole } from "../../../../redux/players/playersSlice";
import {
  PlayerToInclude,
  RatingFilter,
  RightPanelMode,
  Scope,
  Squad,
  SquadBuilderPlayerType,
} from "../../../../redux/squadBuilder/squadBuilderUtils";
import { Tactic } from "../../../../redux/tactics/tacticsSlice";
import { ActiveFilter, FilterKey } from "../components/FiltersBar/FiltersBar";
import { ChemstyleMode } from "./ChemstyleMode/ChemstyleMode";
import { InputMode } from "./InputMode/InputMode";
import { MoreFiltersMode } from "./MoreFiltersMode/MoreFiltersMode";
import { PlayerFiltersMode } from "./PlayerFiltersMode/PlayerFiltersMode";
import { PlayerMode } from "./PlayerMode/PlayerMode";
import { PlayerRoleMode } from "./PlayerRoleMode/PlayerRoleMode";
import { SuggestedSquadsMode } from "./SuggestedSquadsMode/SuggestedSquadsMode";

type Props = {
  rightPanelMode: RightPanelMode;
  statusBuildSquad: APIStatus;
  budget: number;
  excludeIconsHeroes: boolean;
  ratingFilter: RatingFilter;
  scope: Scope;
  selectedCountries: number[];
  selectedLeagues: number[];
  selectedClubs: number[];
  selectedPnPs: number[];
  selectedMetals: number[];
  squad: Squad;
  selectedVersions: number[];
  recommendedPlayers: SquadBuilderPlayerType[];
  handleDidAddPlayer: (
    playerRoleId: number,
    player?: SquadBuilderPlayerType,
    temp?: boolean,
    metaRatings?: MetaRating[],
    focus?: string,
  ) => void;
  handleClickedClose: () => void;
  handleGetPlayerRecommendations: () => void;
  playerRoles: PlayerRole[];
  handleClickedBuildSquads: () => void;
  handleSortBuiltSquads: (sortBy: "meta-rating" | "chemistry") => void;
  handleRemoveTempSquad: () => void;
  handleAddSquad: (index: number, temp: boolean) => void;
  handleClickedCloseSuggestedSquadsMode: () => void;
  handleClickedCloseChemstyleMode?: () => void;
  handleClickedClosePlayerFiltersMode?: () => void;
  handleClickedCloseMoreFiltersMode?: () => void;
  handleSelectChemstyle?: (
    chemstyleId: number,
    metaRatings: MetaRating[],
  ) => void;
  builtSquads: Squad[];
  playerFilters: (RangeFilterType | SelectorFilterType)[];
  handleUpdateFilter: (f: UpdateFilterType) => void;
  handleClearCustomFilter: (key: string) => void;
  // Clears every filter on the selected spot
  handleClearPlayerFilters?: () => void;
  handleClearFilter?: (key: FilterKey) => void;
  numberOfPlayersWithFilters?: number;
  playersToInclude?: PlayerToInclude[];
  changingPitchItemOnIndex?: number;
  availablePlayerRoles?: Record<string, boolean>;
  statusGetRecommendedPlayers?: APIStatus;
  handleUpdateFormation?: (formationId: string, isTemp: boolean) => void;
  handleRemoveTempPlayer?: () => void;
  handleBudgetUpdated: (budget: number) => void;
  handleExcludeIconsHeroesUpdated: (excludeIconsHeroes: boolean) => void;
  handleMinRatingUpdated: (minRating: number) => void;
  handleMaxRatingUpdated: (maxRating: number) => void;
  handleScopeChange: (scope: Scope) => void;
  handleSelectedCountry: (id: number) => void;
  handleSelectedLeague: (id: number) => void;
  handleSelectedClub: (id: number) => void;
  handleSelectedPnP: (id: number) => void;
  handleSelectedMetal: (id: number) => void;
  handleSelectedVersion: (id: number) => void;
  handleClickMoreFilters?: () => void;
  onBoardingStep?: number;
  chemOptimizing?: boolean;
  tabletOrLargerMode?: boolean;
  desktopMode?: boolean;
  savedSquads: SavedSquad[];
  loadedSaveSquadIndex: number;
  handleLoadSquad: (index: number) => void;
  handleAddNewSquad: () => void;
  handleDeleteSquad: (squadId: string) => Promise<void>;
  activeFilters: ActiveFilter[];
  chosenTactic: Tactic;
  scrollPastTopBar: boolean;
  benchItems: SquadBuilderPlayerType[];
  handlePositionAndRoleChanged: (
    position: string,
    roleId: number,
    metaRatings: MetaRating[],
  ) => void;
  includeLoanPlayers: boolean;
  handleIncludeLoanPlayersUpdated: (includeLoanPlayers: boolean) => void;
  handleBestFormationChange: (isBestFormation: boolean) => void;
  isBestFormationOn: boolean;
  activeFilterCount?: number;
  handleClearAllFilters?: () => void;
  highlightFilters?: boolean;
};

export const RightPanel = ({
  rightPanelMode,
  statusBuildSquad,
  budget,
  excludeIconsHeroes,
  ratingFilter,
  scope,
  selectedCountries,
  selectedLeagues,
  selectedClubs,
  selectedPnPs,
  selectedVersions,
  selectedMetals,
  recommendedPlayers,
  handleDidAddPlayer,
  handleRemoveTempPlayer,
  handleClickedClose,
  handleGetPlayerRecommendations,
  numberOfPlayersWithFilters,
  handleClearFilter,
  playersToInclude,
  changingPitchItemOnIndex,
  availablePlayerRoles,
  statusGetRecommendedPlayers,
  playerRoles,
  playerFilters,
  handleUpdateFilter,
  handleClearCustomFilter,
  handleClearPlayerFilters,
  handleUpdateFormation,
  handleClickedBuildSquads,
  onBoardingStep,
  handleClickedCloseSuggestedSquadsMode,

  handleClickedCloseChemstyleMode,
  handleSelectChemstyle,
  builtSquads,
  handleSortBuiltSquads,
  handleRemoveTempSquad,
  handleAddSquad,
  squad,
  handleClickMoreFilters,
  handleBudgetUpdated,
  handleExcludeIconsHeroesUpdated,
  handleMinRatingUpdated,
  handleMaxRatingUpdated,
  handleScopeChange,
  handleSelectedCountry,
  handleSelectedLeague,
  handleSelectedClub,
  handleSelectedPnP,
  handleSelectedMetal,
  handleSelectedVersion,
  handleClickedClosePlayerFiltersMode,
  handleClickedCloseMoreFiltersMode,
  chemOptimizing,
  tabletOrLargerMode,
  desktopMode,
  savedSquads,
  loadedSaveSquadIndex,
  handleLoadSquad,
  handleAddNewSquad,
  handleDeleteSquad,
  activeFilters,
  chosenTactic,
  benchItems,
  scrollPastTopBar,
  handlePositionAndRoleChanged,
  includeLoanPlayers,
  handleIncludeLoanPlayersUpdated,
  handleBestFormationChange,
  isBestFormationOn,
  activeFilterCount,
  handleClearAllFilters,
  highlightFilters,
}: Props) => {
  const topToPage = useSelector(topToPageSelector);
  const formations = useSelector(formationsSelector);
  const player = useMemo(
    () =>
      squad.players[changingPitchItemOnIndex] ||
      playersToInclude?.[changingPitchItemOnIndex]?.player,
    [squad.players, changingPitchItemOnIndex, playersToInclude],
  );
  const benchPlayer = useMemo(
    () => benchItems[changingPitchItemOnIndex - 11],
    [benchItems, changingPitchItemOnIndex],
  );
  const playerRoleId = useMemo(
    () => player?.playerRoleId || benchPlayer?.playerRoleId,
    [player?.playerRoleId, benchPlayer?.playerRoleId],
  );
  const playerChemistry = useMemo(
    () =>
      squad.chemistry.playersChemistry?.[changingPitchItemOnIndex] ||
      player?.chemistry ||
      0,
    [
      squad.chemistry.playersChemistry,
      changingPitchItemOnIndex,
      player?.chemistry,
    ],
  );
  const selectedChemstyle = useMemo(
    () => player?.chemstyleId,
    [player?.chemstyleId],
  );

  // Get position and uniquePosition from formations in Redux
  // For pitch positions (0-10), positions are fixed and come from formations
  // For bench positions (11+), positions can be changed by user and come from benchItems
  const position = useMemo(() => {
    if (changingPitchItemOnIndex > 10) {
      // Bench position - get from bench item (can be changed by user)
      return benchPlayer?.position;
    }
    // Pitch position - get from formations (fixed)
    return formations?.[chosenTactic.teamTactics.formationId]?.positions?.[
      changingPitchItemOnIndex
    ];
  }, [
    chosenTactic.teamTactics.formationId,
    changingPitchItemOnIndex,
    formations,
    benchPlayer?.position,
  ]);

  const uniquePosition = useMemo(() => {
    // uniquePosition is only used for pitch positions, not bench
    if (changingPitchItemOnIndex > 10) {
      return undefined;
    }
    return formations?.[chosenTactic.teamTactics.formationId]
      ?.uniquePositions?.[changingPitchItemOnIndex];
  }, [
    chosenTactic.teamTactics.formationId,
    changingPitchItemOnIndex,
    formations,
  ]);

  return (
    <section
      className={cn(
        "bg-gray-100 w-full desktop:w-[260px] min-h-screen mobile:min-h-50 sb-v2-lg:w-[352px] top-0 shrink-0 bg-side-panel shadow-right-panel bottom-0 pb-6 px-4 mobile:px-0 z-20 overflow-y-auto",
        // Above the spotlight's page dim; the panel dims itself around the card.
        highlightFilters && "z-[106]",
        scrollPastTopBar && desktopMode
          ? "fixed right-[max(0px,calc((100%-2000px)/2))]"
          : "mobile:absolute right-0",
        scrollPastTopBar && desktopMode && topToPage.tailwindString,
      )}
    >
      {rightPanelMode === "add-player-role" && position ? (
        <PlayerRoleMode
          handleClickedClose={handleClickedClose}
          handleRoleChanged={(playerRoleId, metaRatings, focus) => {
            handleDidAddPlayer(
              playerRoleId,
              undefined,
              false,
              metaRatings,
              focus,
            );
          }}
          playerRoles={playerRoles}
          changingPitchItemOnIndex={changingPitchItemOnIndex}
          availablePlayerRoles={availablePlayerRoles}
          player={changingPitchItemOnIndex > 10 ? benchPlayer : player}
          chemistry={playerChemistry}
          position={position}
          uniquePosition={uniquePosition}
          selectedFocus={
            chosenTactic.playerRoles[changingPitchItemOnIndex]?.focus
          }
          selectedPlayerRoleId={
            chosenTactic.playerRoles[changingPitchItemOnIndex]?.playerRoleId
          }
          isBench={changingPitchItemOnIndex > 10}
          handlePositionAndRoleChanged={handlePositionAndRoleChanged}
        />
      ) : rightPanelMode === "add-player" ? (
        <PlayerMode
          playerRoles={playerRoles}
          changingPitchItemOnIndex={changingPitchItemOnIndex}
          playersToInclude={playersToInclude}
          playerRoleId={playerRoleId}
          playersOnPitch={
            squad.players?.length > 0
              ? squad.players
              : playersToInclude?.map((p) => p.player)
          }
          playersOnBench={benchItems}
          recommendedPlayers={recommendedPlayers}
          availablePlayerRoles={availablePlayerRoles}
          handleClickedClose={handleClickedClose}
          handleDidAddPlayer={handleDidAddPlayer}
          handleRemoveTempPlayer={handleRemoveTempPlayer}
          handleGetPlayerRecommendations={handleGetPlayerRecommendations}
          statusGetRecommendedPlayers={statusGetRecommendedPlayers}
          tabletOrLargerMode={tabletOrLargerMode}
          desktopMode={desktopMode}
          formationPosition={position}
        />
      ) : rightPanelMode === "suggested-squads" ? (
        <SuggestedSquadsMode
          onBoardingStep={onBoardingStep}
          builtSquads={builtSquads}
          handleSortBuiltSquads={handleSortBuiltSquads}
          handleAddSquad={handleAddSquad}
          handleRemoveTempSquad={handleRemoveTempSquad}
          handleClickedClose={handleClickedCloseSuggestedSquadsMode}
          formationId={chosenTactic.teamTactics.formationId}
        />
      ) : rightPanelMode === "add-player-filters" ? (
        <PlayerFiltersMode
          handleClickedClose={handleClickedClosePlayerFiltersMode}
          filters={playerFilters}
          handleUpdateFilter={handleUpdateFilter}
          handleClearCustomFilter={handleClearCustomFilter}
          handleClearPlayerFilters={handleClearPlayerFilters}
        />
      ) : rightPanelMode === "select-chem-style" ? (
        <ChemstyleMode
          player={player}
          chemistry={playerChemistry}
          handleClickedClose={handleClickedCloseChemstyleMode}
          handleSelectChemstyle={handleSelectChemstyle}
          selectedChemstyle={selectedChemstyle}
        />
      ) : rightPanelMode === "add-more-filters" ? (
        <MoreFiltersMode
          selectedPnPs={selectedPnPs}
          selectedVersions={selectedVersions}
          selectedMetals={selectedMetals}
          excludeIconsHeroes={excludeIconsHeroes}
          includeLoanPlayers={includeLoanPlayers}
          handleClickedClose={handleClickedCloseMoreFiltersMode}
          handleSelectedPnP={handleSelectedPnP}
          handleSelectedVersion={handleSelectedVersion}
          handleExcludeIconsHeroesUpdated={handleExcludeIconsHeroesUpdated}
          handleIncludeLoanPlayersUpdated={handleIncludeLoanPlayersUpdated}
          handleSelectedMetal={handleSelectedMetal}
        />
      ) : (
        <InputMode
          statusBuildSquad={statusBuildSquad}
          budget={budget}
          ratingFilter={ratingFilter}
          scope={scope}
          selectedCountries={selectedCountries}
          selectedLeagues={selectedLeagues}
          selectedClubs={selectedClubs}
          numberOfPlayersWithFilters={numberOfPlayersWithFilters}
          selectedFormation={chosenTactic.teamTactics.formationId}
          handleUpdateFormation={handleUpdateFormation}
          handleClickedBuildSquads={handleClickedBuildSquads}
          onBoardingStep={onBoardingStep}
          handleBudgetUpdated={handleBudgetUpdated}
          handleMinRatingChange={handleMinRatingUpdated}
          handleMaxRatingChange={handleMaxRatingUpdated}
          handleScopeChange={handleScopeChange}
          handleSelectedCountry={handleSelectedCountry}
          handleSelectedLeague={handleSelectedLeague}
          handleSelectedClub={handleSelectedClub}
          chemOptimizing={chemOptimizing}
          desktopMode={desktopMode}
          savedSquads={savedSquads}
          loadedSaveSquadIndex={loadedSaveSquadIndex}
          handleLoadSquad={handleLoadSquad}
          handleAddNewSquad={handleAddNewSquad}
          handleDeleteSquad={handleDeleteSquad}
          handleClickMoreFilters={handleClickMoreFilters}
          handleBestFormationChange={handleBestFormationChange}
          isBestFormationOn={isBestFormationOn}
          activeFilterCount={activeFilterCount}
          handleClearAllFilters={handleClearAllFilters}
          highlightFilters={highlightFilters}
        />
      )}
    </section>
  );
};
